import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { ChevronLeft, Phone, Mail, MapPin, Globe, MessageCircle, Facebook, Instagram, Twitter } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from 'sonner';

const LocalDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [local, setLocal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    fetchLocal();
  }, [slug]);

  const fetchLocal = async () => {
    try {
      const localesQuery = query(
        collection(db, 'locales'),
        where('slug', '==', slug)
      );
      const localesSnapshot = await getDocs(localesQuery);

      if (!localesSnapshot.empty) {
        const localData = {
          id: localesSnapshot.docs[0].id,
          ...localesSnapshot.docs[0].data()
        };
        setLocal(localData);
      } else {
        setLocal(null);
      }
    } catch (error) {
      console.error('Error fetching local:', error);
      toast.error('Error al cargar el local');
      setLocal(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!local) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-bold mb-4">Local no encontrado</h2>
          <Link to="/locales">
            <Button>Volver a locales</Button>
          </Link>
        </div>
      </div>
    );
  }

  const images = local.imagenesAdicionales || [local.imagenPrincipal];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-8"
          >
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="gap-2"
            >
              <ChevronLeft size={20} />
              Volver
            </Button>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="space-y-4">
                <div className="relative rounded-xl overflow-hidden shadow-2xl">
                  <img
                    src={images[selectedImage]}
                    alt={local.nombre}
                    className="w-full h-full object-cover"
                  />
                </div>

                {images.length > 1 && (
                  <div className="grid grid-cols-3 gap-4">
                    {images.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImage(index)}
                        className={`relative h-24 rounded-lg overflow-hidden transition-all ${selectedImage === index
                          ? 'ring-4 ring-primary scale-105'
                          : 'opacity-60 hover:opacity-100'
                          }`}
                      >
                        <img
                          src={image}
                          alt={`${local.nombre} ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-6"
            >
              <div>
                <Badge className="mb-4">{local.categoria}</Badge>
                <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
                  {local.nombre}
                </h1>
                <p className="text-lg text-muted-foreground">
                  {local.descripcion}
                </p>
              </div>

              <Separator />

              {local.productos && (
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-3">Productos</h3>
                  <p className="text-muted-foreground">{local.productos}</p>
                </div>
              )}

              {local.servicios && (
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-3">Servicios</h3>
                  <p className="text-muted-foreground">{local.servicios}</p>
                </div>
              )}

              {local.keywords && local.keywords.length > 0 && (
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-3">Palabras Clave</h3>
                  <div className="flex flex-wrap gap-2">
                    {local.keywords.map((keyword, index) => (
                      <Badge key={index} variant="outline">
                        {keyword}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <Separator />

              <Card>
                <CardContent className="p-6 space-y-4">
                  <h3 className="text-xl font-heading font-semibold mb-4">Información de Contacto</h3>

                  {local.telefono && (
                    <div className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors">
                      <Phone size={20} className="flex-shrink-0" />
                      <a href={`tel:${local.telefono}`}>{local.telefono}</a>
                    </div>
                  )}

                  {local.email && (
                    <div className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors">
                      <Mail size={20} className="flex-shrink-0" />
                      <a href={`mailto:${local.email}`}>{local.email}</a>
                    </div>
                  )}

                  {local.ubicacion && (
                    <div className="flex items-center gap-3 text-muted-foreground">
                      <MapPin size={20} className="flex-shrink-0" />
                      <span>{local.ubicacion}</span>
                    </div>
                  )}

                  {local.whatsapp && (
                    <Button
                      className="w-full bg-[#25D366] hover:bg-[#20BA5A] mt-4"
                      onClick={() => window.open(`https://wa.me/${local.whatsapp}`, '_blank')}
                    >
                      <MessageCircle className="mr-2" size={20} />
                      Contactar por WhatsApp
                    </Button>
                  )}

                  {local.redesSociales && (
                    <div className="pt-4">
                      <h4 className="text-sm font-medium mb-3">Redes Sociales</h4>
                      <div className="flex gap-3">
                        {local.redesSociales.facebook && (
                          <a
                            href={local.redesSociales.facebook}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-muted rounded-lg hover:bg-primary hover:text-primary-foreground transition-colors"
                          >
                            <Facebook size={20} />
                          </a>
                        )}
                        {local.redesSociales.instagram && (
                          <a
                            href={local.redesSociales.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-muted rounded-lg hover:bg-accent hover:text-accent-foreground transition-colors"
                          >
                            <Instagram size={20} />
                          </a>
                        )}
                        {local.redesSociales.twitter && (
                          <a
                            href={local.redesSociales.twitter}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-muted rounded-lg hover:bg-secondary hover:text-secondary-foreground transition-colors"
                          >
                            <Twitter size={20} />
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LocalDetailPage;